/**
 * Klok van de overlegpagina — pure functies, getest in __tests__/timer.test.js.
 * De klok loopt vanaf de GEPLANDE start (Allen et al. 2018: te laat beginnen kost kwaliteit), niet
 * vanaf het moment dat de voorzitter op start drukte. Servertijd corrigeert de clientklok.
 */

/** [{id, minuten}] → [{blok_id, startMs, eindMs}] vanaf de geplande start. */
export function blokSchema(geplandStartMs, blokken) {
  let t = geplandStartMs;
  return (blokken || []).map((b) => {
    const start = t;
    t += (Number(b.minuten) || 0) * 60000;
    return { blok_id: b.id, startMs: start, eindMs: t };
  });
}

/** Klokstand voor het actieve blok en het geheel. Alle seconden zijn afgerond naar beneden. */
export function klokStand(nowMs, schema, huidigBlokId, totaalMin, geplandStartMs) {
  const totaalEindMs = geplandStartMs + (Number(totaalMin) || 0) * 60000;
  const blok = schema.find((s) => s.blok_id === huidigBlokId) || null;
  const naarSec = (ms) => Math.floor(ms / 1000);
  const resterendTotaalSec = naarSec(totaalEindMs - nowMs);
  if (!blok) {
    return { blok: null, resterendBlokSec: null, overTijdBlok: false, resterendTotaalSec, overTijdTotaal: resterendTotaalSec < 0, voortgangBlok: 0, gestart: nowMs >= geplandStartMs };
  }
  const resterendBlokSec = naarSec(blok.eindMs - nowMs);
  const duur = blok.eindMs - blok.startMs;
  const voortgangBlok = duur > 0 ? Math.min(1, Math.max(0, (nowMs - blok.startMs) / duur)) : 0;
  return {
    blok, resterendBlokSec, overTijdBlok: resterendBlokSec < 0, resterendTotaalSec, overTijdTotaal: resterendTotaalSec < 0,
    voortgangBlok, gestart: nowMs >= geplandStartMs,
  };
}

/** −90 → "-1:30", 65 → "1:05", 3600 → "60:00". */
export function mmss(sec) {
  if (sec == null || Number.isNaN(sec)) return '–:––';
  const neg = sec < 0;
  const a = Math.abs(sec);
  const m = Math.floor(a / 60);
  const s = a % 60;
  return `${neg ? '-' : ''}${m}:${String(s).padStart(2, '0')}`;
}

/** Vóór de start: "over 2 d 11 u" / "over 3 u 12 min" / "over 4:59" (laatste 5 minuten als aftelling). */
export function totStart(msTotStart) {
  if (msTotStart <= 0) return null;
  const sec = Math.floor(msTotStart / 1000);
  if (sec <= 5 * 60) return `over ${mmss(sec)}`;
  const d = Math.floor(sec / 86400);
  const u = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `over ${d} d ${u} u`;
  if (u > 0) return `over ${u} u ${m} min`;
  return `over ${m} min`;
}

/** Verschil server − client in ms, zodat elke browser dezelfde klok laat zien. */
export function klokCorrectie(serverNowIso, clientNowMs = Date.now()) {
  if (!serverNowIso) return 0;
  const s = Date.parse(serverNowIso);
  return Number.isNaN(s) ? 0 : s - clientNowMs;
}

/**
 * Loopt de klok nog? Alleen voor een niet-afgesloten sessie van vandaag of later. Een sessie van vóór
 * vandaag die nooit is afgesloten (bleef op 'bezig' staan) telde anders dagen door: "8671:14 over tijd".
 * `vandaagIso` = YYYY-MM-DD in NL-tijd (van de server).
 */
export function klokLoopt(sessie, vandaagIso) {
  if (!sessie || sessie.status === 'afgesloten') return false;
  if (!vandaagIso || !sessie.datum) return true;
  return String(sessie.datum).slice(0, 10) >= String(vandaagIso).slice(0, 10);
}
